import { User } from 'firebase/auth'
import {
  UploadStaging,
  DriverMatchResult,
  NormalizedDataRow,
  UnmatchedIdentifierAction,
  ProcessingProgress
} from '@/types'
import { FirestoreService } from './firestoreService'
import { DriverMasterService } from './driverMasterService'
import { EarningsService } from './earningsService'
import { CreateDriverMasterData, DriverMaster } from '@/types/driverMaster'
import { CreateEarningData, Earning } from '@/types/earnings'

export interface DriverActionResult {
  rowIndex: number
  action: 'created' | 'linked' | 'skipped' | 'error'
  driverId?: string
  driverName?: string
  error?: string
}

export interface DriverActionResults {
  totalActions: number
  createdDrivers: number
  linkedDrivers: number
  skippedRows: number
  errors: number
  results: DriverActionResult[]
  newDriverIds: string[]
}

export interface EarningsCreationResult {
  rowIndex: number
  driverId: string
  driverName: string
  earningId?: string
  action: 'created' | 'skipped' | 'error'
  error?: string
  grossSalary?: number
}

export interface EarningsCreationResults {
  stagingId: string
  totalRows: number
  successCount: number
  errorCount: number
  skippedCount: number
  totalGrossSalary: number
  createdEarnings: EarningsCreationResult[]
  errors: Array<{ rowIndex: number; error: string }>
  summary: {
    averageGrossSalary: number
    driverCount: number
    platformBreakdown: Record<string, number>
  }
}

/**
 * Service for processing staging data into final earnings
 * Handles driver actions and earnings creation from staged upload data
 */
export class StagingEarningsProcessingService {
  private user: User
  private firestoreService: FirestoreService

  constructor(user: User) {
    this.user = user
    this.firestoreService = new FirestoreService(user)
  }

  /**
   * Process driver actions based on user choices
   * Creates new drivers, links existing ones, handles aliases
   */
  async processDriverActions(
    stagingId: string,
    userChoices: {
      unmatchedActions: Map<number, UnmatchedIdentifierAction>
      perfectMatchOverrides: Map<number, UnmatchedIdentifierAction>
      ignoredRows: Set<number>
    },
    onProgress?: (progress: ProcessingProgress) => void
  ): Promise<DriverActionResults> {
    // Get staging data
    const staging = await this.firestoreService.getUploadStaging(stagingId)
    if (!staging || !staging.driverMatches) {
      throw new Error('Staging data or driver matches not found')
    }

    const results: DriverActionResult[] = []
    const newDriverIds: string[] = []
    let createdDrivers = 0
    let linkedDrivers = 0
    let skippedRows = 0
    let errors = 0

    const totalActions = userChoices.unmatchedActions.size + userChoices.perfectMatchOverrides.size

    // Process unmatched drivers (create new or link existing)
    let currentAction = 0
    for (const [rowIndex, action] of userChoices.unmatchedActions) {
      currentAction++

      if (onProgress) {
        onProgress({
          uploadId: staging.uploadId,
          currentBatch: currentAction,
          totalBatches: totalActions,
          processedRows: currentAction,
          totalRows: totalActions,
          percentage: Math.round((currentAction / totalActions) * 100),
          currentStatus: `Bearbetar föraråtgärd ${currentAction} av ${totalActions}`,
          startTime: new Date(),
          estimatedTimeRemaining: 0
        })
      }

      try {
        const driverMatch = staging.driverMatches.find(match => match.rowIndex === rowIndex)
        if (!driverMatch) {
          results.push({
            rowIndex,
            action: 'error',
            error: 'Driver match not found for row'
          })
          errors++
          continue
        }

        if (action.action === 'create_new') {
          // Create new driver
          const driverId = await this.createNewDriver(driverMatch, action, staging.platform)
          const driverName = action.newDriverName || driverMatch.extractedName || 'Unknown'

          // Link the new driver to the staging data
          await this.linkToNewDriver(stagingId, rowIndex, driverId, driverName)

          results.push({
            rowIndex,
            action: 'created',
            driverId,
            driverName
          })
          newDriverIds.push(driverId)
          createdDrivers++

        } else if (action.action === 'select_existing') {
          // Link to existing driver
          const selectedDriverId = action.selectedDriverId
          if (!selectedDriverId) {
            throw new Error('Selected driver ID required for select_existing action')
          }

          // Get driver name from the selected driver
          const selectedDriver = await DriverMasterService.getDriver(this.user, selectedDriverId)
          const driverName = selectedDriver?.primaryName || 'Unknown Driver'

          // Create alias for manually linked driver if needed
          const hasAliasForPlatform = selectedDriver?.aliases?.some(alias =>
            alias.platform.toLowerCase() === staging.platform.toLowerCase())

          if (!hasAliasForPlatform) {
            try {
              const aliasData: any = {
                platform: staging.platform
              }

              if (driverMatch.identifiers.uuid) {
                aliasData.uuid = driverMatch.identifiers.uuid
              }

              if (driverMatch.identifiers.name) {
                aliasData.name = driverMatch.identifiers.name
              }

              await DriverMasterService.createDriverAlias(this.user, selectedDriverId, aliasData)
            } catch (aliasError) {
              // Alias creation failure doesn't block the linking
            }
          }

          // Update the driver match to link to existing driver
          await this.linkToExistingDriver(stagingId, rowIndex, selectedDriverId)

          results.push({
            rowIndex,
            action: 'linked',
            driverId: selectedDriverId,
            driverName
          })
          linkedDrivers++

        } else if (action.action === 'skip') {
          results.push({
            rowIndex,
            action: 'skipped'
          })
          skippedRows++
        }

      } catch (error) {
        console.error(`[StagingEarningsProcessing] Error processing action for row ${rowIndex}:`, error)
        results.push({
          rowIndex,
          action: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        })
        errors++
      }
    }

    // Process perfect match overrides
    for (const [rowIndex, action] of userChoices.perfectMatchOverrides) {
      currentAction++

      if (onProgress) {
        onProgress({
          uploadId: staging.uploadId,
          currentBatch: currentAction,
          totalBatches: totalActions,
          processedRows: currentAction,
          totalRows: totalActions,
          percentage: Math.round((currentAction / totalActions) * 100),
          currentStatus: `Bearbetar föraråtgärd ${currentAction} av ${totalActions}`,
          startTime: new Date(),
          estimatedTimeRemaining: 0
        })
      }

      try {
        const driverMatch = staging.driverMatches.find(match => match.rowIndex === rowIndex)
        if (!driverMatch) {
          results.push({
            rowIndex,
            action: 'error',
            error: 'Driver match not found for row'
          })
          errors++
          continue
        }

        if (action.action === 'create_new') {
          // Create new driver
          const driverId = await this.createNewDriver(driverMatch, action, staging.platform)
          const driverName = action.newDriverName || driverMatch.extractedName || 'Unknown'

          // Link the new driver to the staging data
          await this.linkToNewDriver(stagingId, rowIndex, driverId, driverName)

          results.push({
            rowIndex,
            action: 'created',
            driverId,
            driverName
          })
          newDriverIds.push(driverId)
          createdDrivers++

        } else if (action.action === 'select_existing' && action.selectedDriverId) {
          // Get driver name from the selected driver
          const selectedDriver = await DriverMasterService.getDriver(this.user, action.selectedDriverId)
          const driverName = selectedDriver?.primaryName || 'Unknown Driver'

          // Create alias for manually linked driver if needed
          const hasAliasForPlatform = selectedDriver?.aliases?.some(alias =>
            alias.platform.toLowerCase() === staging.platform.toLowerCase())

          if (!hasAliasForPlatform) {
            try {
              const aliasData: any = {
                platform: staging.platform
              }

              if (driverMatch.identifiers.uuid) {
                aliasData.uuid = driverMatch.identifiers.uuid
              }

              if (driverMatch.identifiers.name) {
                aliasData.name = driverMatch.identifiers.name
              }

              await DriverMasterService.createDriverAlias(this.user, action.selectedDriverId, aliasData)
            } catch (aliasError) {
              // Alias creation failure doesn't block the linking
            }
          }

          await this.linkToExistingDriver(stagingId, rowIndex, action.selectedDriverId)
          results.push({
            rowIndex,
            action: 'linked',
            driverId: action.selectedDriverId,
            driverName
          })
          linkedDrivers++
        }
      } catch (error) {
        console.error(`[StagingEarningsProcessing] Error processing override for row ${rowIndex}:`, error)
        results.push({
          rowIndex,
          action: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        })
        errors++
      }
    }

    // Process auto-accepted matches that need aliases
    // These are drivers that were matched but don't have an alias for this platform yet
    const autoAcceptedMatches = staging.driverMatches.filter(match =>
      match.hasMatch &&
      match.needsAlias &&
      !userChoices.unmatchedActions.has(match.rowIndex) &&
      !userChoices.perfectMatchOverrides.has(match.rowIndex) &&
      !userChoices.ignoredRows.has(match.rowIndex)
    )

    for (const match of autoAcceptedMatches) {
      if (match.matchedDriverId) {
        try {
          const aliasData: any = {
            platform: staging.platform
          }

          if (match.identifiers.uuid) {
            aliasData.uuid = match.identifiers.uuid
          }

          if (match.identifiers.name) {
            aliasData.name = match.identifiers.name
          }

          await DriverMasterService.createDriverAlias(
            this.user,
            match.matchedDriverId,
            aliasData
          )
        } catch (error) {
          // Don't fail the entire process if alias creation fails
        }
      }
    }

    return {
      totalActions,
      createdDrivers,
      linkedDrivers,
      skippedRows,
      errors,
      results,
      newDriverIds
    }
  }

  /**
   * Create earnings from processed staging data
   */
  async createEarningsFromStaging(
    stagingId: string,
    ignoredRows: Set<number>,
    onProgress?: (progress: ProcessingProgress) => void
  ): Promise<EarningsCreationResults> {
    // Get staging data
    const staging = await this.firestoreService.getUploadStaging(stagingId)
    if (!staging || !staging.driverMatches) {
      throw new Error('Staging data or driver matches not found')
    }

    const results: EarningsCreationResult[] = []
    const errors: Array<{ rowIndex: number; error: string }> = []
    let successCount = 0
    let errorCount = 0
    let skippedCount = 0
    let totalGrossSalary = 0

    // Filter out ignored rows and rows without matched drivers
    const processableMatches = staging.driverMatches.filter(match =>
      !ignoredRows.has(match.rowIndex) && match.matchedDriverId
    )

    for (let i = 0; i < processableMatches.length; i++) {
      const match = processableMatches[i]

      if (onProgress) {
        onProgress({
          uploadId: staging.uploadId,
          currentBatch: i + 1,
          totalBatches: processableMatches.length,
          processedRows: i + 1,
          totalRows: processableMatches.length,
          percentage: Math.round(((i + 1) / processableMatches.length) * 100),
          currentStatus: `Skapar inkomst ${i + 1} av ${processableMatches.length}`,
          startTime: new Date(),
          estimatedTimeRemaining: 0
        })
      }

      try {
        // Get normalized data row
        const normalizedRow = staging.normalizedData.find(row => row.rowIndex === match.rowIndex)
        if (!normalizedRow) {
          throw new Error(`Normalized data not found for row ${match.rowIndex}`)
        }

        // Create earning
        const earningResult = await this.createSingleEarning(
          staging,
          match,
          normalizedRow
        )

        results.push(earningResult)

        if (earningResult.action === 'created') {
          successCount++
          if (earningResult.grossSalary) {
            totalGrossSalary += earningResult.grossSalary
          }
        } else if (earningResult.action === 'error') {
          errorCount++
          errors.push({
            rowIndex: match.rowIndex,
            error: earningResult.error || 'Unknown error'
          })
        } else {
          skippedCount++
        }

      } catch (error) {
        console.error(`[StagingEarningsProcessing] Error creating earning for row ${match.rowIndex}:`, error)
        const errorMsg = error instanceof Error ? error.message : 'Unknown error'

        results.push({
          rowIndex: match.rowIndex,
          driverId: match.matchedDriverId || '',
          driverName: match.matchedDriverName || 'Unknown',
          action: 'error',
          error: errorMsg
        })

        errors.push({
          rowIndex: match.rowIndex,
          error: errorMsg
        })
        errorCount++
      }
    }

    // Calculate summary statistics
    const driverCount = new Set(results.filter(r => r.action === 'created').map(r => r.driverId)).size
    const averageGrossSalary = successCount > 0 ? totalGrossSalary / successCount : 0

    const platformBreakdown: Record<string, number> = {}
    const platform = staging.platform
    platformBreakdown[platform] = successCount

    const finalResults: EarningsCreationResults = {
      stagingId,
      totalRows: processableMatches.length,
      successCount,
      errorCount,
      skippedCount,
      totalGrossSalary,
      createdEarnings: results,
      errors,
      summary: {
        averageGrossSalary,
        driverCount,
        platformBreakdown
      }
    }

    // Mark staging as completed
    await this.markStagingComplete(stagingId, finalResults)

    // Update the upload document status to reflect earnings were created
    if (staging.uploadId) {
      await this.firestoreService.updateUpload(staging.uploadId, {
        status: 'earnings_created'
      })
    }

    return finalResults
  }

  /**
   * Create a new driver from driver match and action
   */
  private async createNewDriver(
    driverMatch: DriverMatchResult,
    action: UnmatchedIdentifierAction,
    platform: string
  ): Promise<string> {
    const driverData: CreateDriverMasterData = {
      primaryName: action.newDriverName || driverMatch.extractedName || 'Unknown Driver',
      email: driverMatch.identifiers.email || undefined,
      phone: driverMatch.identifiers.phone || undefined,
      notes: `Created from staging upload on ${new Date().toISOString()}`
    }

    const newDriver = await DriverMasterService.createDriver(this.user, driverData)

    // Create a single alias for the platform with both UUID and name (if available)
    const hasUuid = driverMatch.identifiers.uuid && driverMatch.identifiers.uuid.trim() !== ''
    const hasName = driverMatch.identifiers.name && driverMatch.identifiers.name.trim() !== ''

    if (hasUuid || hasName) {
      const aliasData: any = {
        platform: platform
      }

      if (hasUuid) {
        aliasData.uuid = driverMatch.identifiers.uuid.trim()
      }

      if (hasName) {
        aliasData.name = driverMatch.identifiers.name.trim()
      }

      try {
        await DriverMasterService.createDriverAlias(this.user, newDriver.id, aliasData)
      } catch (error) {
        // Alias creation failure doesn't block driver creation
      }
    }

    return newDriver.id
  }

  /**
   * Link driver match to newly created driver
   */
  private async linkToNewDriver(
    stagingId: string,
    rowIndex: number,
    newDriverId: string,
    driverName: string
  ): Promise<void> {
    // Update the staging data to link this row to the new driver
    const staging = await this.firestoreService.getUploadStaging(stagingId)
    if (!staging || !staging.driverMatches) {
      throw new Error('Staging data not found')
    }

    const updatedMatches = staging.driverMatches.map(match => {
      if (match.rowIndex === rowIndex) {
        return {
          ...match,
          matchedDriverId: newDriverId,
          matchedDriverName: driverName,
          hasMatch: true,
          matchedVia: 'created' as const
        }
      }
      return match
    })

    await this.firestoreService.updateUploadStaging(stagingId, {
      driverMatches: updatedMatches
    })
  }

  /**
   * Link driver match to existing driver
   */
  private async linkToExistingDriver(
    stagingId: string,
    rowIndex: number,
    existingDriverId: string
  ): Promise<void> {
    // Update the staging data to link this row to the existing driver
    const staging = await this.firestoreService.getUploadStaging(stagingId)
    if (!staging || !staging.driverMatches) {
      throw new Error('Staging data not found')
    }

    const updatedMatches = staging.driverMatches.map(match => {
      if (match.rowIndex === rowIndex) {
        return {
          ...match,
          matchedDriverId: existingDriverId,
          hasMatch: true,
          matchedVia: 'manual' as const
        }
      }
      return match
    })

    await this.firestoreService.updateUploadStaging(stagingId, {
      driverMatches: updatedMatches
    })
  }

  /**
   * Create a single earning from staging data
   */
  private async createSingleEarning(
    staging: UploadStaging,
    match: DriverMatchResult,
    normalizedRow: NormalizedDataRow
  ): Promise<EarningsCreationResult> {
    if (!match.matchedDriverId) {
      throw new Error('Driver ID is required for earnings creation')
    }

    // Extract earnings data
    const { earningsData } = normalizedRow.extractedData

    // Create earning data (only fields that match CreateEarningData interface)
    const earningData: CreateEarningData = {
      driverId: match.matchedDriverId || '',
      uploadSessionId: staging.uploadId || '',
      period: staging.period, // Use period from staging (which comes from upload)
      platform: staging.platform,
      grossEarnings: earningsData.grossEarnings || 0,
      netEarnings: earningsData.netEarnings || 0,
      tips: earningsData.tips || 0
    }

    // Add optional fields if they have values
    if (earningsData.cashReceived && earningsData.cashReceived > 0) {
      earningData.cashReceived = earningsData.cashReceived
    }

    // Create the earning
    const earning = await EarningsService.createEarning(this.user, earningData)

    return {
      rowIndex: match.rowIndex,
      driverId: match.matchedDriverId,
      driverName: match.matchedDriverName || 'Unknown',
      earningId: earning.id,
      action: 'created'
      // Note: grossSalary is calculated later using aggregated calculation in the salary report
    }
  }

  /**
   * Mark staging as completed with results
   */
  private async markStagingComplete(
    stagingId: string,
    results: EarningsCreationResults
  ): Promise<void> {
    await this.firestoreService.updateUploadStaging(stagingId, {
      status: 'completed' as const,
      completedAt: new Date(),
      processingResults: {
        totalRows: results.totalRows,
        successCount: results.successCount,
        errorCount: results.errorCount,
        totalGrossSalary: results.totalGrossSalary,
        summary: results.summary
      }
    })
  }
}